import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { supabase } from '../config/supabase';

interface AuthCallbackProps {
  onAuthSuccess: (user: any) => void;
  isDark: boolean;
}

const AuthCallback: React.FC<AuthCallbackProps> = ({ onAuthSuccess, isDark }) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('🔐 Processing auth callback...');
        console.log('📍 Current URL:', window.location.href);
        
        // Supabase automatically handles the token from URL hash
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('❌ Session error:', sessionError);
          setError('Failed to process authentication. Please try again.');
          setLoading(false);
          return;
        }

        if (session?.user) {
          console.log('✅ Auth callback successful, user:', session.user.email);
          console.log('🧹 Clearing auth hash from URL...');
          
          // IMPORTANT: Clear the auth hash IMMEDIATELY to prevent re-processing
          // Use replace to avoid adding to browser history
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          
          console.log('📍 URL after clear:', window.location.href);
          console.log('⏱️ Waiting 500ms before triggering auth success...');
          
          // Give Supabase a moment to sync everything, then trigger auth success
          setTimeout(() => {
            console.log('✨ Calling onAuthSuccess callback...');
            onAuthSuccess(session.user);
          }, 500);
        } else {
          console.warn('⚠️ No user session found in callback');
          setError('Authentication failed. No user session found.');
          setLoading(false);
        }
      } catch (err) {
        console.error('❌ Auth callback error:', err);
        setError('An error occurred during authentication.');
        setLoading(false);
      }
    };

    handleCallback();
  }, [onAuthSuccess]);

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        isDark
          ? 'bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950'
          : 'bg-gradient-to-br from-zinc-50 via-white to-zinc-50'
      }`}>
        <div className="w-full max-w-md px-6">
          <div className="flex justify-center mb-8">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-red-600 via-red-500 to-orange-600 flex items-center justify-center shadow-lg ${
              isDark ? 'shadow-red-500/30' : 'shadow-red-300/40'
            }`}>
              <Sparkles className="w-8 h-8 text-white" />
            </div>
          </div>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Authentication Error</h2>
            <p className={`text-sm mb-6 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
              {error}
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-2 rounded-lg bg-gradient-to-r from-red-600 to-rose-600 text-white font-semibold hover:shadow-lg transition-all"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
      isDark
        ? 'bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950'
        : 'bg-gradient-to-br from-zinc-50 via-white to-zinc-50'
    }`}>
      <div className="w-full max-w-md px-6">
        <div className="flex justify-center mb-8">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-red-600 via-orange-600 to-rose-600 flex items-center justify-center shadow-lg ${
            isDark ? 'shadow-red-500/30' : 'shadow-red-300/40'
          }`}>
            <Sparkles className="w-8 h-8 text-white" />
          </div>
        </div>
        <div className="text-center">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent mb-4">
            Completing Sign In
          </h2>
          <p className={`text-sm mb-8 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            Please wait while we authenticate your account...
          </p>
          <div className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
              Signing you in...
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
